/*
 * Copyright (c) 2020. Legalbird eine Marke der R&S Internet Jewels GmbH
 */

import React from "react";
import FormFader from "../Common/Fader/FormFader";
import Paper from "@mui/material/Paper";
import Grid from "@mui/material/Grid";
import FormSubmit from "../Common/FormSubmit/FormSubmit";
import CheckIcon from "../../../assets/icons/common/toggleable/IconCheck";
import CrossIcon from "../../../assets/icons/common/toggleable/IconClose";
import Hidden from "@mui/material/Hidden";
import { Box, InputAdornment } from "@mui/material";
import { useForm } from "../../../provider/Form/FormProvider";
import Group from "../../Wizard/Group";
import { requiredValidator } from "../../../services/validationRules";
import DialogModal from "../../DialogModal/DialogModal";
import { useLocation, useNavigate } from "react-router-dom";
import Typography from "@mui/material/Typography";
import FormLegend from "../Common/FormLegend/FormLegend";
import NumberFieldConfigured from "../ConfiguredFormElements/NumberFieldConfigured/NumberFieldConfigured";
import { AbstractCase } from "../../../types/Entities/AbstractCase";
import FormSubmitProps from "../../../types/FormSubmitProps";
import wizardStyle from "../wizardStyle";
import InfoIconButton from "../../Button/InfoIconButton";

type IncomeEmploymentProps = {
  submitProps: FormSubmitProps;
  product: AbstractCase;
};

export default function IncomeEmployment({ submitProps, product }: IncomeEmploymentProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const { values, handleSubmit } = useForm();

  const modalOpen = () => {
    navigate(location.pathname, { state: { incomeEmploymentModalOpen: true } });
  };

  const modalClose = () => {
    navigate(-1);
  };

  return (
    <Paper sx={wizardStyle.paper} elevation={4}>
      <FormFader>
        <Box component={"form"} sx={wizardStyle.wizardForm} onSubmit={handleSubmit}>
          <Hidden smUp>
            <Typography variant={"h4"} sx={wizardStyle.headline}>
              Einnahmen
            </Typography>
          </Hidden>
          <Grid container justifyContent={"center"}>
            <Grid item xs={12}>
              <Group
                type={"radio"}
                name={"hasIncomeEmployment"}
                question={"Erhalten Sie als Angestellter Lohn oder Gehalt?"}
                modalContent={
                  <p>
                    Hier geht es um Lohn oder Gehalt aus einem angestellten Arbeitsverhältnis. Auch gemeint sind Lohnfortzahlungen oder Nebentätigkeiten wie
                    "Mini-Jobs" und ähnliches.
                  </p>
                }
                options={[
                  {
                    labelText: "Nein",
                    labelIcon: <CrossIcon />,
                    labelIconActive: <CrossIcon active />,
                    value: "no",
                  },
                  {
                    labelText: "Ja",
                    labelIcon: <CheckIcon />,
                    labelIconActive: <CheckIcon active />,
                    value: "yes",
                  },
                ]}
                validators={[requiredValidator("Bitte wählen Sie eine der angezeigten Antworten aus")]}
              />
            </Grid>
            {values.hasIncomeEmployment === "yes" && (
              <>
                <Grid item xs={12} sx={wizardStyle.gridTopSpaced}>
                  <FormLegend>
                    Bitte geben Sie hier den <strong>Brutto</strong>lohn des letzten Monats an. Urlaubs-, Weihnachtsgeld oder andere einmalige Leistungen können
                    Sie später unter "Sonstige Einnahmen" angeben.
                  </FormLegend>
                </Grid>
                <NumberFieldConfigured
                  caseData={product}
                  fieldPath={"incomeEmploymentAmount"}
                  required
                  fieldType={"money"}
                  numberType={"float"}
                  inputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <InfoIconButton onClick={modalOpen} />
                      </InputAdornment>
                    ),
                  }}
                />
              </>
            )}
          </Grid>
          <FormSubmit {...submitProps} />
        </Box>
      </FormFader>
      <DialogModal open={!!(location.state && location.state.incomeEmploymentModalOpen)} onClose={modalClose} title={"Wo finde ich meinen Bruttolohn?"}>
        <p>
          Den monatlichen <strong>Brutto</strong>lohn können Sie Ihrem Arbeitsvertrag oder Ihrer Lohnabrechnung entnehmen.
        </p>
      </DialogModal>
    </Paper>
  );
}
